/**
 * AddressAutocomplete - Google Places autocomplete-backed address input.
 *
 * i18n (Phase 5):
 *   - Uses addressAutocomplete.* namespace for the default placeholder.
 *   - The placeholder prop, when explicitly passed by the caller, takes
 *     precedence — callers in BookingForm already pass already-translated
 *     `booking.departureAddress` / `booking.destinationAddress` strings.
 *   - The Google Places script `language=fr` query string is intentionally
 *     left as-is: the autocomplete suggestions come from Google in the page
 *     language; switching the script language at runtime is out of scope.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { loadGoogleMaps } from '../lib/googleMapsLoader';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

export const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  className = "",
  id,
  ...props
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const suppressOnChange = useRef(false);

  // Hold latest onChange/onPlaceSelect in refs so the place_changed listener
  // always reads them via .current. The Autocomplete instance is built ONCE
  // on isLoaded — keeping the parent's inline arrow functions out of the
  // effect's dep array avoids the "tear down + rebuild on every render" cycle
  // that previously caused selections from the dropdown to silently no-op
  // (the listener was attached to a freshly cleaned-up instance).
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectRef.current = onPlaceSelect; }, [onPlaceSelect]);

  const effectivePlaceholder = placeholder || t('addressAutocomplete.defaultPlaceholder');

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps({ apiKey: GOOGLE_MAPS_API_KEY, language: 'fr' })
      .then(() => { if (!cancelled) setIsLoaded(true); })
      .catch((err) => { console.error('Google Maps load failed:', err); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || !window.google) return;

    const options = {
      componentRestrictions: { country: 'fr' },
      fields: ['formatted_address', 'geometry', 'name'],
      types: ['geocode', 'establishment']
    };

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        // Google Places returns descriptive `name` (e.g. "Aéroport Nantes
        // Atlantique (NTE)", "Gare de Nantes Sud") separately from the postal
        // `formatted_address` (e.g. "44346 Bouguenais, France"). For named POIs
        // — airports, train stations, hotels — the postal-only string loses the
        // landmark identity. We prefix `name` unless it's already inside
        // `formatted_address` (which is the case for plain street/number
        // results where `name` duplicates the start of `formatted_address`).
        const formatted = place.formatted_address;
        const name = (place.name || '').trim();
        const formattedLower = formatted.toLowerCase();
        const address = name && !formattedLower.startsWith(name.toLowerCase())
          ? `${name}, ${formatted}`
          : formatted;
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        suppressOnChange.current = true;
        onChangeRef.current?.(address);
        onPlaceSelectRef.current?.({ address, lat, lng });
        setTimeout(() => { suppressOnChange.current = false; }, 500);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded]);

  // Sync value to DOM input without triggering onChange
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value || '';
    }
  }, [value]);

  return (
    <Input
      ref={inputRef}
      type="text"
      defaultValue={value}
      onChange={(e) => {
        if (!suppressOnChange.current) {
          onChange(e.target.value);
        }
      }}
      placeholder={effectivePlaceholder}
      className={`bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 rounded-xl transition-colors h-11 ${className}`}
      id={id}
      data-testid={id}
      {...props}
    />
  );
};

export default AddressAutocomplete;
